import MedicationCombobox from '@/components/common/medication-combobox'
import VariationSelect from '@/components/common/variation-select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  completeSelection,
  durationChoices,
  EMPTY_LINK_SELECTION,
  groupByProductDose,
  initialSelectionFor,
  type LinkVariationSelection,
  productDoseKeyOf,
  productDoseOptions,
  quantityChoices,
  resolveVariation
} from '@/lib/link-variation-options'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import type { Medication } from '@/types/data'
import { format, isAfter } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { DatePicker } from '../datepicker'
import TextareaWithLabel from '../textarea'

import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import { SetDateIcon, TooltipWrapper } from './previous-orders'
import { useToast } from '@/hooks/use-toast'
import { useGetSuspenseMedications } from '@/services/medications'
import { usePreviousOrderDates } from '@/hooks/usePreviousOrderDates'
import { getCurrentUKDate, isMpsPlaceholderProduct, isTodayInUK } from '@/lib/utils'

export default function LinkPrescriptionDetails({ isEditable }: { isEditable: boolean }) {
  const {
    medicationIssueDate,
    setMedicationIssueDate,
    medicationDirection,
    setMedicationDirection,
    medication,
    setMedication,
    isTemporaryReplacement,
    setIsTemporaryReplacement,
    isLastSubscriptionOrder,
    setIsLastSubscriptionOrder
  } = useMedicationRequestStore()

  const { toast } = useToast()
  const { data: medRequestData } = useSuspenseCurrentMedication()
  const { advancedPreviousOrderDate, lastIssuedDate } = usePreviousOrderDates()

  const isFutureDate = isAfter(medicationIssueDate, new Date())

  const showNoMedicationDirectionBadge = isEditable && !medicationDirection

  const { data: medications } = useGetSuspenseMedications()

  // Unlike the standard layout there is no cadence filter: Duration is one of the
  // choices, so every variation of the product stays on offer.
  const productDoseGroups = useMemo(
    () => groupByProductDose((medications ?? []).filter(entry => !isMpsPlaceholderProduct(entry.productName))),
    [medications]
  )

  const orderedItemId = medRequestData.orderedItem?.id
  const orderedVariation = useMemo(
    () => (orderedItemId ? medications?.find(entry => entry.variantHash === orderedItemId) : undefined),
    [medications, orderedItemId]
  )

  const parsedProductDoseOptions = useMemo(
    () => productDoseOptions(productDoseGroups, orderedVariation && productDoseKeyOf(orderedVariation)),
    [productDoseGroups, orderedVariation]
  )

  // The store's `medication` is set only while all three steps resolve to a variation, and is null
  // otherwise — which is what keeps Approve disabled until every field is chosen.
  const [selection, setSelection] = useState<LinkVariationSelection>(EMPTY_LINK_SELECTION)

  const selectedVariations = selection.productDose ? (productDoseGroups.get(selection.productDose) ?? []) : []
  const durationOptions = durationChoices(selectedVariations)
  const quantityOptions = selection.duration !== null ? quantityChoices(selectedVariations, selection.duration) : []
  const isSelectionIncomplete =
    selection.productDose === null || selection.duration === null || selection.quantity === null

  // Same comparison approve uses to decide whether a changeProduct is sent — the flag rides on it.
  const selectedHash = medication?.variantHash || medication?.productHash || ''
  const isProductChanged = !!selectedHash && selectedHash !== orderedItemId

  const applyMedication = (variation: Medication | null) => {
    if (!variation) {
      setMedication(null)
      return
    }
    if (variation.variantHash === medication?.variantHash) return

    setMedicationDirection(variation.type === 'OTC' ? '' : (variation.medicationDirections ?? ''))
    setMedication(variation)
  }

  const onSelectionChange = (next: LinkVariationSelection) => {
    const completed = completeSelection(productDoseGroups, next)
    setSelection(completed)
    applyMedication(resolveVariation(productDoseGroups, completed, [medication?.variantHash, orderedItemId]))
  }

  useEffect(() => {
    if (!medications?.length) return

    const initial = initialSelectionFor(productDoseGroups, orderedVariation, orderedItemId)
    const variation = resolveVariation(productDoseGroups, initial, [orderedItemId])

    setSelection(initial)
    setMedicationDirection(medRequestData.orderedItem.directions || variation?.medicationDirections || '')
    setMedication(variation)
  }, [medRequestData, medications])

  return (
    <div data-testid='prescription-details'>
      <p
        id='prescription-details'
        className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] font-feature-settings-liga-off font-feature-settings-clig-off mb-6'
      >
        Prescription details
      </p>
      <p id='select-medication' className='text-[#1D1D1D]  text-base font-medium leading-[170%] mb-4'>
        Select medication
      </p>
      <div className='flex flex-col gap-6'>
        <div data-testid='link-product-dose'>
          <MedicationCombobox
            isDisabled={!isEditable}
            options={parsedProductDoseOptions}
            onValueChange={productDose => onSelectionChange({ ...selection, productDose })}
            title='Product & Dose'
            defaultValue={selection.productDose ?? undefined}
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <VariationSelect
            testId='link-duration'
            title='Duration'
            placeholder={selection.productDose === null ? 'Select a product & dose first' : 'Select a duration...'}
            options={durationOptions}
            value={selection.duration}
            onValueChange={duration => onSelectionChange({ ...selection, duration })}
            isDisabled={!isEditable || durationOptions.length <= 1}
          />
          <VariationSelect
            testId='link-quantity'
            title='Quantity'
            placeholder={selection.duration === null ? 'Select a duration first' : 'Select a quantity...'}
            options={quantityOptions}
            value={selection.quantity}
            onValueChange={quantity => onSelectionChange({ ...selection, quantity })}
            isDisabled={!isEditable || quantityOptions.length <= 1}
          />
        </div>
      </div>

      {isEditable && isSelectionIncomplete && (
        <div
          data-testid='link-selection-incomplete'
          className='mt-4 rounded-2xl border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'
        >
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            Please <b>select a product & dose, duration and quantity</b> before approving the medication request.
          </p>
        </div>
      )}

      {isProductChanged && (
        <div className='flex items-center gap-1 mt-4'>
          <Checkbox
            id='temporary-replacement-checkbox'
            data-testid='temporary-replacement'
            checked={isTemporaryReplacement}
            disabled={!isEditable}
            onCheckedChange={checked => setIsTemporaryReplacement(checked === true)}
          />
          <label htmlFor='temporary-replacement-checkbox' className='ml-2 text-sm text-[#1D1D1D]'>
            Temporary replacement
          </label>
        </div>
      )}

      <div className='flex items-center gap-1 mt-4'>
        <Checkbox
          id='last-subscription-order-checkbox'
          data-testid='last-subscription-order'
          checked={isLastSubscriptionOrder}
          disabled={!isEditable}
          onCheckedChange={checked => setIsLastSubscriptionOrder(checked === true)}
        />
        <label htmlFor='last-subscription-order-checkbox' className='ml-2 text-sm text-[#1D1D1D]'>
          Last subscription order
        </label>
      </div>

      <p id='medication-direction' className=' text-[#1D1D1D] text-base font-medium leading-[170%] my-4'>
        Medication direction
      </p>
      {showNoMedicationDirectionBadge && medication?.type !== 'OTC' && (
        <div className='mb-4 rounded-2xl mt-4 border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This medication doesn't have any directions. Please
            <b>{''} add directions before approving</b> the medication request.
          </p>
        </div>
      )}

      <TextareaWithLabel
        isDisabled={!isEditable}
        value={medicationDirection}
        title='Direction'
        onChange={e => setMedicationDirection(e.target.value)}
        hasError={showNoMedicationDirectionBadge && medication?.type !== 'OTC'}
      />

      <div className='flex flex-col gap-[2.18rem]'>
        <p id='issue-date' className=' text-[#1D1D1D] text-base font-medium leading-[170%] my-4'>
          Issue Date
        </p>
        <div
          data-testid='medication-issue-date'
          className='rounded-2xl border border-custom-neutral-200 bg-[#EBEFFA] flex px-6 py-4 justify-between items-center'
        >
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>Medication Issue Date</p>

          <div className='flex flex-col gap-3 w-max'>
            <div className='flex gap-[26px]'>
              <p className='text-[#080F30] text-base font-semibold leading-[170%]'>
                {format(medicationIssueDate, 'd MMMM yyyy')} {isTodayInUK(medicationIssueDate) ? '(Today)' : ''}
              </p>

              <DatePicker
                isDisabled={!isEditable}
                fromDate={getCurrentUKDate()}
                date={medicationIssueDate}
                setDate={picked => {
                  const theDate = new Date(picked ?? new Date())
                  // default at noon to prevent timezone date flips
                  theDate.setHours(12, 0, 0, 0)
                  setMedicationIssueDate(theDate)
                }}
              />
            </div>
          </div>
        </div>

        {lastIssuedDate && (
          <div className='flex items-center gap-4'>
            <span className='font-poppins text-base leading-7 font-bold text-[#1D1D1D]'>Last issued date:</span>
            <span className='font-poppins text-base leading-7 text-[#4E545E]'>
              {format(lastIssuedDate, 'd MMMM yyyy')}
            </span>
            <TooltipWrapper
              trigger={
                <button
                  disabled={!isEditable}
                  type='button'
                  className='flex items-center gap-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
                  onClick={() => {
                    setMedicationIssueDate(advancedPreviousOrderDate)
                    toast({
                      title: 'Issue date set',
                      description: `Issue date set to [${format(advancedPreviousOrderDate, 'dd MMMM yyyy')}]`
                    })
                  }}
                >
                  <SetDateIcon />
                </button>
              }
              content={
                <p className='text-white text-xs font-medium leading-4'>Set issue date to 12 weeks from this date</p>
              }
            />
          </div>
        )}
      </div>

      {isFutureDate && (
        <div className='rounded-2xl mt-4 border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This request is scheduled for approval on <b>{format(medicationIssueDate, 'MMMM d, yyyy')}.</b>
          </p>
        </div>
      )}
    </div>
  )
}
